<div class="p-4 sm:p-6 max-w-7xl mx-auto space-y-6">
  <!-- Header -->
  <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
    <div>
      <h1 class="text-2xl font-bold text-gray-900 tracking-tight">Bulk Scrap Inquiries</h1>
      <p class="text-sm text-gray-500 mt-1">Review and manage inbound commercial &amp; bulk scrap leads</p>
    </div>
    <button
      type="button"
      (click)="loadRequests()"
      [disabled]="isLoading"
      class="inline-flex items-center justify-center min-h-[44px] px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 shadow-sm transition disabled:opacity-50">
      <svg *ngIf="isLoading" class="animate-spin -ml-1 mr-2 h-4 w-4 text-emerald-600" fill="none" viewBox="0 0 24 24" aria-hidden="true">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      Refresh
    </button>
  </div>

  <!-- Inline feedback messages (no popups) -->
  <div *ngIf="errorMessage" role="alert" class="p-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-center justify-between">
    <span>{{ errorMessage }}</span>
    <button type="button" (click)="errorMessage = ''" aria-label="Dismiss error message"
      class="min-h-[44px] min-w-[44px] text-red-500 hover:text-red-700 font-bold text-lg">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>
  <div *ngIf="successMessage" role="status" class="p-4 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm flex items-center justify-between">
    <span>{{ successMessage }}</span>
    <button type="button" (click)="dismissSuccess()" aria-label="Dismiss success message"
      class="min-h-[44px] min-w-[44px] text-emerald-500 hover:text-emerald-700 font-bold text-lg">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>

  <!-- Filter tabs -->
  <div class="flex flex-wrap gap-2 border-b border-gray-200 pb-3" role="group" aria-label="Filter by status">
    <button
      type="button"
      (click)="onStatusFilterChange('')"
      [attr.aria-pressed]="selectedStatus === ''"
      [ngClass]="selectedStatus === '' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700'"
      class="min-h-[44px] px-4 rounded-full text-sm font-semibold transition hover:opacity-90">
      All statuses<span *ngIf="selectedStatus === ''"> ({{ totalCount }})</span>
    </button>
    <button
      *ngFor="let status of statusOptions"
      type="button"
      (click)="onStatusFilterChange(status)"
      [attr.aria-pressed]="selectedStatus === status"
      [ngClass]="selectedStatus === status ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700'"
      class="min-h-[44px] px-4 rounded-full text-sm font-semibold transition hover:opacity-90">
      {{ statusMeta[status].label }}<span *ngIf="selectedStatus === status"> ({{ totalCount }})</span>
    </button>
  </div>

  <!-- Loading skeleton -->
  <div *ngIf="isLoading" class="space-y-4" aria-busy="true" aria-label="Loading bulk scrap inquiries">
    <div *ngFor="let i of [1, 2, 3]" class="animate-pulse bg-white border border-gray-200 rounded-xl p-6 h-28"></div>
  </div>

  <!-- Empty state -->
  <div *ngIf="!isLoading && !errorMessage && requests.length === 0" class="text-center py-16 bg-white border border-gray-200 rounded-xl">
    <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
    </svg>
    <h2 class="mt-2 text-sm font-medium text-gray-900">No bulk scrap requests found</h2>
    <p class="mt-1 text-sm text-gray-500" data-testid="empty-message">{{ emptyMessage }}</p>
    <button *ngIf="selectedStatus" type="button" (click)="onStatusFilterChange('')"
      class="mt-4 min-h-[44px] px-4 text-sm font-semibold text-emerald-700 hover:underline">
      Show all statuses
    </button>
  </div>

  <!-- Requests list (Desktop table + Mobile cards) -->
  <div *ngIf="!isLoading && requests.length > 0" class="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
    <!-- Desktop table -->
    <div class="hidden md:block overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-200 text-left text-sm">
        <thead class="bg-gray-50 text-gray-600 font-medium">
          <tr>
            <th scope="col" class="py-3 px-4">Date / Source</th>
            <th scope="col" class="py-3 px-4">Organization &amp; Contact</th>
            <th scope="col" class="py-3 px-4">Address / Pincode</th>
            <th scope="col" class="py-3 px-4">Categories &amp; Volume</th>
            <th scope="col" class="py-3 px-4">Status</th>
            <th scope="col" class="py-3 px-4"><span class="sr-only">Actions</span></th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-200 text-gray-800">
          <tr *ngFor="let req of requests; trackBy: trackById" class="hover:bg-gray-50 transition">
            <td class="py-4 px-4 align-top whitespace-nowrap">
              <div class="font-medium text-gray-900">{{ req.createdAt | date: 'shortDate' }}</div>
              <div class="text-xs text-gray-500 mb-1">{{ req.createdAt | date: 'shortTime' }}</div>
              <app-bulk-scrap-source-badge [source]="req.source" />
            </td>
            <td class="py-4 px-4 align-top">
              <div class="font-bold text-gray-900">{{ req.organizationName }}</div>
              <div class="text-gray-600">{{ req.contactPerson }}</div>
              <a [href]="'tel:' + req.contactPhone" class="text-xs text-emerald-700 hover:underline font-semibold block mt-1">{{ req.contactPhone }}</a>
              <div *ngIf="req.contactEmail" class="text-xs text-gray-500">{{ req.contactEmail }}</div>
            </td>
            <td class="py-4 px-4 align-top max-w-xs">
              <div class="text-gray-800 line-clamp-2">{{ req.address }}</div>
              <div *ngIf="req.pincode" class="text-xs text-gray-500 mt-1">PIN: {{ req.pincode }}</div>
              <div *ngIf="req.locationName" class="text-xs text-gray-400">{{ req.locationName }}</div>
            </td>
            <td class="py-4 px-4 align-top">
              <div class="font-semibold text-gray-900">{{ req.estimatedVolume }} {{ req.estimatedVolumeUnit }}</div>
              <div class="flex flex-wrap gap-1 mt-1">
                <span *ngFor="let cat of req.categories" class="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">{{ cat.categoryName }}</span>
              </div>
              <div *ngIf="req.frequency" class="text-xs text-gray-500 mt-1">Freq: {{ req.frequency }}</div>
            </td>
            <td class="py-4 px-4 align-top">
              <app-bulk-scrap-status-editor
                *ngIf="editingRequestId === req.id; else statusView"
                [request]="req"
                (saved)="onStatusSaved(req, $event)"
                (cancelled)="editingRequestId = null" />
              <ng-template #statusView>
                <app-bulk-scrap-status-badge [status]="req.status" />
                <button *ngIf="canManageStatus" type="button" (click)="editingRequestId = req.id"
                  class="block min-h-[44px] text-sm text-emerald-700 hover:text-emerald-900 font-semibold hover:underline">
                  Change status
                </button>
              </ng-template>
            </td>
            <td class="py-4 px-4 align-top text-right">
              <button type="button" (click)="openDetails(req, $event)"
                [attr.aria-label]="'View details for ' + req.organizationName"
                class="min-h-[44px] px-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 whitespace-nowrap">
                View details
                <span *ngIf="req.photoUrls?.length" class="ml-1 text-xs text-gray-500">({{ req.photoUrls.length }} photos)</span>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Mobile cards -->
    <ul class="md:hidden divide-y divide-gray-200">
      <li *ngFor="let req of requests; trackBy: trackById" class="p-4 space-y-3">
        <div class="flex items-start justify-between gap-2">
          <div>
            <h2 class="font-bold text-gray-900 text-base">{{ req.organizationName }}</h2>
            <div class="text-sm text-gray-600">{{ req.contactPerson }}</div>
          </div>
          <app-bulk-scrap-status-badge [status]="req.status" />
        </div>
        <div class="text-xs text-gray-500 flex items-center gap-3">
          <span>{{ req.createdAt | date: 'mediumDate' }}</span>
          <app-bulk-scrap-source-badge [source]="req.source" />
        </div>
        <div class="text-sm text-gray-700">
          <a [href]="'tel:' + req.contactPhone" class="inline-flex items-center min-h-[44px] text-emerald-700 font-semibold">{{ req.contactPhone }}</a>
          <p class="text-xs text-gray-600">{{ req.address }}</p>
        </div>
        <div class="bg-gray-50 rounded-lg p-2.5 text-xs space-y-1">
          <div class="font-semibold text-gray-900">Estimated volume: {{ req.estimatedVolume }} {{ req.estimatedVolumeUnit }}</div>
          <div class="flex flex-wrap gap-1">
            <span *ngFor="let cat of req.categories" class="px-2 py-0.5 bg-white border border-gray-200 rounded text-[11px] text-gray-700">{{ cat.categoryName }}</span>
          </div>
        </div>
        <app-bulk-scrap-status-editor
          *ngIf="editingRequestId === req.id"
          class="block"
          [request]="req"
          (saved)="onStatusSaved(req, $event)"
          (cancelled)="editingRequestId = null" />
        <div *ngIf="editingRequestId !== req.id" class="pt-2 border-t border-gray-100 flex flex-wrap gap-2">
          <button type="button" (click)="openDetails(req, $event)"
            class="flex-1 min-h-[44px] px-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white">
            View details
          </button>
          <button *ngIf="canManageStatus" type="button" (click)="editingRequestId = req.id"
            class="flex-1 min-h-[44px] px-3 rounded-lg text-sm font-semibold text-emerald-700 bg-emerald-50">
            Change status
          </button>
        </div>
      </li>
    </ul>

    <div class="p-4 border-t border-gray-200 flex justify-between items-center bg-gray-50">
      <app-pagination [totalPages]="totalPages" [pageSize]="pageSize" [page]="pageIndex + 1" (pageChange)="onPageChange($event)" />
    </div>
  </div>

  <!-- Details slide-over -->
  <app-bulk-scrap-request-details
    *ngIf="selectedRequest"
    [request]="selectedRequest"
    [canManageStatus]="canManageStatus"
    (closed)="closeDetails()"
    (statusSaved)="onStatusSaved(selectedRequest, $event)"
    (photoOpened)="onDrawerPhoto($event)" />

  <!-- Photo viewer (stacks above the drawer) -->
  <div *ngIf="previewPhotoUrl" (click)="closePhoto()" class="fixed inset-0 z-[60] bg-black/75 flex items-center justify-center p-4">
    <div role="dialog" aria-modal="true" aria-labelledby="bulk-photo-dialog-title"
      class="relative max-w-3xl max-h-[90vh] bg-white rounded-xl overflow-hidden shadow-2xl" (click)="$event.stopPropagation()">
      <div class="flex justify-between items-center p-2 pl-3 border-b border-gray-200">
        <h2 id="bulk-photo-dialog-title" class="text-xs font-semibold text-gray-600">Scrap preview photo</h2>
        <button #photoCloseButton type="button" (click)="closePhoto()" aria-label="Close photo preview"
          class="min-h-[44px] min-w-[44px] text-gray-400 hover:text-gray-700 font-bold text-lg">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <img [src]="previewPhotoUrl" class="max-h-[75vh] w-auto object-contain mx-auto" alt="Full-size scrap photo" />
    </div>
  </div>
</div>
